import { useAuthStore } from '@/stores/useAuthStore.js'
import { usePermissionStore } from '@/stores/usePermissionStore.js'
import { layouts } from 'chart.js'
import { isEmpty } from 'lodash'
import { nextTick } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import { isFeatureEnabled } from '@/features/feature-flags.js'
// Lazy load layouts
const AdminLayout = () => import('@/layouts/AdminLayout.vue')
const ClientLayout = () => import('@/layouts/ClientLayout.vue')

const VehicleOrderLayout = () => import('@/layouts/VehicleOrderLayout.vue')
const RatesEntryLayout = () => import('@/layouts/ToyotaRatesLayout.vue')
const PermissionLayout = () => import('@/layouts/PermissionLayout.vue')

// Same component for all four admin quotation routes, so switching between them reuses the
// instance (the page watches route.name).
const AdminQuotationList = () =>
  isFeatureEnabled('quotations')
    ? import('@/features/quotations/pages/AdminQuotationListPage.vue')
    : import('@/pages/admin/quotation/QuotationList.vue')

// Same for the three client quotation routes.
const ClientQuotationList = () =>
  isFeatureEnabled('quotations')
    ? import('@/features/quotations/pages/ClientQuotationListPage.vue')
    : import('@/pages/client/quotation/QuotationList.vue')

const AdminBookingList = () =>
  isFeatureEnabled('vehicle-orders')
    ? import('@/features/vehicle-orders/pages/AdminBookingListPage.vue')
    : import('@/pages/admin/vehicle-order/BookingOrderList.vue')

const AdminVofList = () =>
  isFeatureEnabled('vehicle-orders')
    ? import('@/features/vehicle-orders/pages/VofBatchListPage.vue')
    : import('@/pages/admin/vehicle-order/VehicleOrderList.vue')

const AdminBookingItemList = () =>
  isFeatureEnabled('vehicle-orders')
    ? import('@/features/vehicle-orders/pages/AdminBookingItemListPage.vue')
    : import('@/pages/admin/vehicle-order/BookingItemList.vue')

const AdminTruckingList = () =>
  isFeatureEnabled('vehicle-orders')
    ? import('@/features/vehicle-orders/pages/AdminTruckingListPage.vue')
    : import('@/pages/admin/vehicle-order/TruckingList.vue')

const AdminOtherList = () =>
  isFeatureEnabled('vehicle-orders')
    ? import('@/features/vehicle-orders/pages/OtherListPage.vue')
    : import('@/pages/admin/vehicle-order/OtherList.vue')

const ClientBookingList = () =>
  isFeatureEnabled('vehicle-orders')
    ? import('@/features/vehicle-orders/pages/ClientBookingListPage.vue')
    : import('@/pages/client/vehicle-order/BookingOrderList.vue')

const ClientTruckingList = () =>
  isFeatureEnabled('vehicle-orders')
    ? import('@/features/vehicle-orders/pages/ClientTruckingListPage.vue')
    : import('@/pages/client/vehicle-order/TruckingList.vue')

const ClientOtherList = () =>
  isFeatureEnabled('vehicle-orders')
    ? import('@/features/vehicle-orders/pages/OtherListPage.vue')
    : import('@/pages/client/vehicle-order/OtherList.vue')

const routes = [
  {
    path: '/',
    name: 'login',
    component: () => import('@/pages/Login.vue'),
  },
  {
    path: '/otp',
    name: 'otp',
    meta: { requiresAuth: true },
    component: () => import('@/pages/OTP.vue'),
  },
  {
    path: '/sign-up',
    name: 'signUp',
    component: () => import('@/pages/SignUp.vue'),
  },
  {
    path: '/register/:key',
    name: 'ClientRegistration',
    component: () => import('@/pages/ClientForm.vue'),
  },
  {
    path: '/forgot-password',
    name: 'forgotPassword',
    meta: { title: 'Forgot Password' },
    component: () => import('@/pages/ForgotPassword.vue'),
  },

  // Client area
  {
    path: '/client',
    component: ClientLayout,
    meta: { requiresAuth: true, role: 'CL' },
    redirect: { name: 'clientDashboard' },
    children: [
      {
        path: 'dashboard',
        name: 'clientDashboard',
        component: () => import('@/pages/client/Dashboard.vue'),
      },

      // Quotation
      {
        path: 'quotation',
        name: 'clientQuotation',
        component: ClientQuotationList,
        meta: { moduleType: 'main', title: 'All Quotations' },
      },
      {
        path: 'quotation/approved',
        name: 'clientQuotationApproved',
        component: ClientQuotationList,
        meta: {
          moduleType: 'approved',
          title: 'Approved Quotations',
          filters: {
            status: ['AS'],
          },
        },
      },
      {
        path: 'quotation/approvals',
        name: 'clientQuotationApprovals',
        component: ClientQuotationList,
        meta: {
          moduleType: 'approvals',
          title: 'Pending Approvals',
          filters: {
            status: ['CAS'],
          },
        },
      },
      // {
      //   path: 'quotation/returned',
      //   name: 'clientQuotationReturned',
      //   component: () => import('@/pages/admin/quotation/QuotationList.vue'),
      //   meta: {
      //     moduleType: 'returned',
      //     title: 'Returned & Revisions',
      //     filters:{
      //       is_revised:1,
      //       status:['RET']
      //     }
      //   }
      // },

      //VOF
      {
        path: 'vehicle-order',
        name: 'clientVehicleOrder',
        component: VehicleOrderLayout,
        redirect: { name: 'clientBookings' },
        children: [
          {
            path: 'bookings',
            name: 'clientBookings',
            component: ClientBookingList,
          },
          {
            path: 'trucking',
            name: 'clientTrucking',
            component: ClientTruckingList,
          },
          {
            path: 'special',
            name: 'clientOthers',
            component: ClientOtherList,
          },
        ],
      },

      // Invoices
      {
        path: 'invoices',
        name: 'clientInvoiceSummaries',
        component: () => import('@/pages/client/invoice-summary/ClientInvoices.vue'),
      },

      // Deliveries
      {
        path: 'deliveries',
        name: 'clientDeliveryReceipts',
        component: () => import('@/pages/client/deliveries/DeliveriesPage.vue'),
      },

      //Profile
      {
        path: 'profile/:code',
        name: 'clientProfile',
        props: true,
        component: () => import('@/pages/client/profile/ProfilePage.vue'),
      },
    ],
  },

  // Admin area
  {
    path: '/admin',
    component: AdminLayout,
    redirect: { name: 'adminDashboard' },
    meta: { requiresAuth: true, role: 'AD' },
    children: [
      {
        path: 'dashboard',
        name: 'adminDashboard',
        component: () => import('@/pages/admin/Dashboard.vue'),
      },
      // Quotation
      {
        path: 'quotation',
        name: 'adminQuotation',
        component: AdminQuotationList,
        meta: {
          moduleType: 'main',
          title: 'All Quotations',
          permission: ['quotation.list'],
        },
      },
      {
        path: 'quotation/approved',
        name: 'adminQuotationApproved',
        component: AdminQuotationList,
        meta: {
          moduleType: 'approved',
          title: 'Approved Quotations',
          filters: {
            status: ['AS'],
          },
        },
      },
      {
        path: 'quotation/approvals',
        name: 'adminQuotationApprovals',
        component: AdminQuotationList,
        meta: {
          moduleType: 'approvals',
          title: 'Pending Approvals',
          filters: {
            status: ['FAS', 'CKS'],
          },
        },
      },
      {
        path: 'quotation/returned',
        name: 'adminQuotationReturned',
        component: AdminQuotationList,
        meta: {
          moduleType: 'returned',
          title: 'Returned & Revisions',
          filters: {
            is_revised: 1,
            status: ['RET'],
          },
        },
      },
      {
        path: 'client',
        name: 'client',
        redirect: { name: 'clientListing' },
        meta: {
          permission: ['client.list'],
        },
        children: [
          {
            path: 'list',
            name: 'clientListing',
            component: () => import('@/pages/admin/setup/clients/ClientsList.vue'),
          },
          {
            path: ':code',
            name: 'clientDetails',
            props: true,
            component: () => import('@/pages/admin/setup/clients/ClientDetailsForm.vue'),
            meta: {
              permission: ['client.view', 'client.create'],
            },
          },
        ],
      },
      {
        path: 'island',
        name: 'islandList',
        component: () => import('@/pages/admin/setup/island/IslandList.vue'),
        meta: {
          permission: ['island.list'],
        },
      },
      {
        path: 'ports',
        name: 'portsList',
        component: () => import('@/pages/admin/setup/ports/PortsList.vue'),
        meta: {
          permission: ['port.list'],
        },
      },
      {
        path: 'vehicles',
        name: 'vehiclesList',
        component: () => import('@/pages/admin/setup/vehicles/VehicleList.vue'),
        meta: {
          permission: ['vehicle.list'],
        },
      },
      {
        path: 'vessel_setup',
        name: 'VesselSetup',
        redirect: 'vesselList',
        meta: {
          permission: ['vessel.list', 'vessel.route.list', 'vessel.schedule.list'],
        },
        children: [
          {
            path: 'list',
            name: 'vesselList',
            component: () => import('@/pages/admin/setup/vessels/VesselList.vue'),
            meta: {
              permission: ['vessel.list'],
            },
          },
          {
            path: 'routes',
            name: 'vesselRoutes',
            component: () => import('@/pages/admin/setup/vessels/VesselRoutes.vue'),
            meta: {
              permission: ['vessel.route.list'],
            },
          },
          {
            path: 'operations',
            name: 'vesselOperations',
            redirect: { name: 'vesselSchedules' },
            component: () => import('@/pages/admin/setup/vessels/VesselOperations.vue'),
            meta: {
              permission: ['vessel.schedule.list'],
            },
            children: [
              {
                path: 'generate-voyage',
                name: 'generateVesselVoyage',
                component: () => import('@/pages/admin/setup/vessels/GenerateVesselVoyage.vue'),
                meta: {
                  permission: ['vessel.schedule.generate'],
                },
              },
              {
                path: 'schedules',
                name: 'vesselSchedules',
                component: () => import('@/pages/admin/setup/vessels/VesselSchedules.vue'),
              },
            ],
          },
        ],
      },
      {
        path: 'rates',
        name: 'ratesList',
        redirect: { name: 'general' },
        meta: {
          permission: ['rates.list'],
        },
        children: [
          {
            path: 'general',
            name: 'generalRates',
            component: () => import('@/pages/admin/rates/GeneralRatesList.vue'),
          },
          {
            path: 'toyota',
            name: 'toyotaRatesLayout',
            component: () => import('@/layouts/ToyotaRatesLayout.vue'),
            redirect: { name: 'toyotaRatesMain' },
            meta: {
              permission: ['rates.toyota.list'],
            },
            children: [
              {
                path: 'list',
                name: 'toyotaRatesMain',
                component: () => import('@/pages/admin/rates/toyota/ToyotaMainList.vue'),
              },
              {
                path: 'vehicle-classes',
                name: 'toyotaVehicleClasses',
                component: () => import('@/pages/admin/rates/toyota/ToyotaVehicleClasses.vue'),
                meta: {
                  permission: ['rates.toyota.vehicle'],
                },
              },
              {
                path: 'locations',
                name: 'toyotaLocations',
                component: () => import('@/pages/admin/rates/toyota/ToyotaLocationRates.vue'),
                meta: {
                  permission: ['rates.toyota.location'],
                },
              },
            ],
          },
          {
            path: 'special',
            name: 'specialRates',
            component: () => import('@/pages/admin/rates/special/SpecialRateMainList.vue'),
            meta: {
              permission: ['rates.special.list'],
            },
          },
        ],
      },
      {
        path: 'bunker-adjustment-factor',
        name: 'bafList',
        component: () => import('@/pages/admin/setup/baf/BAFPage.vue'),
        meta: {
          permission: ['baf.list'],
        },
      },
      {
        path: 'cargo-types',
        name: 'cargoTypeList',
        component: () => import('@/pages/admin/setup/cargo-types/CargoTypeList.vue'),
      },
      {
        path: 'mode-of-transpo',
        name: 'modList',
        component: () => import('@/pages/admin/setup/mode-of-transpo/ModeOfTransportation.vue'),
      },
      {
        path: 'quotation-terms',
        name: 'quotationTerms',
        component: () => import('@/pages/admin/setup/quotation-terms/QuotationTerms.vue'),
        meta: {
          permission: ['quotationTerms.list'],
        },
      },

      {
        path: 'terms-conditions',
        name: 'termsConditions',
        component: () => import('@/pages/admin/setup/terms-condition/TermsCondition.vue'),
        meta: {
          permission: ['termsCondition.list'],
        },
      },
      {
        path: 'disclaimer',
        name: 'disclaimerList',
        component: () => import('@/pages/admin/setup/disclaimer/DisclaimerList.vue'),
        meta: {
          permission: ['disclaimer.list'],
        },
      },
      {
        path: 'services',
        name: 'servicesList',
        component: () => import('@/pages/admin/setup/services/ServicesList.vue'),
      },
      {
        path: 'subjects',
        name: 'subjectList',
        component: () => import('@/pages/admin/setup/subjects/SubjectList.vue'),
        meta: {
          permission: ['subjects.list'],
        },
      },
      {
        path: 'dr-setup',
        name: 'drSetup',
        component: () => import('@/pages/admin/setup/dr/DRList.vue'),
        meta: {
          permission: ['carrier.list'],
        },
      },
      {
        path: 'logistic-service-provider',
        name: 'logisticProvider',
        component: () => import('@/pages/admin/setup/lsp/LogisticServiceProviderList.vue'),
        meta: {
          permission: ['logistics.list'],
        },
      },

      {
        path: 'permissions',
        name: 'permissions',
        component: PermissionLayout,
        redirect: { name: 'rolePermissions' },
        meta: {
          permission: ['permission.list'],
        },
        children: [
          {
            path: 'role',
            name: 'rolePermissions',
            component: () => import('@/pages/admin/setup/permissions/RolePermission.vue'),
          },
          {
            path: 'user',
            name: 'userPermission',
            component: () => import('@/pages/admin/setup/permissions/UserPermission.vue'),
          },
        ],
      },

      // VOF (Vehicle Order)
      {
        path: 'vehicle-order',
        name: 'adminVehicleOrder',
        component: VehicleOrderLayout,
        redirect: { name: 'adminBookings' },
        meta: {
          permission: ['vof.list', 'booking.list', 'booking_item.list', 'trucking.list'],
        },
        children: [
          {
            path: 'bookings',
            name: 'adminBookings',
            component: AdminBookingList,
            meta: {
              permission: ['booking.list'],
            },
          },
          {
            path: 'orders',
            name: 'adminOrders',
            component: AdminVofList,
            meta: {
              permission: ['vof.list'],
            },
          },
          {
            path: 'booking-items',
            name: 'adminBookingItems',
            component: AdminBookingItemList,
            meta: {
              permission: ['booking_item.list'],
            },
          },
          {
            path: 'trucking',
            name: 'adminTrucking',
            component: AdminTruckingList,
            meta: {
              permission: ['trucking.list'],
            },
          },
          {
            path: 'special',
            name: 'adminOthers',
            component: AdminOtherList,
          },
        ],
      },

      //Load and unload list
      {
        path: 'load-unload-list',
        name: 'loadUnloadList',
        component: () => import('@/pages/admin/load-unload-list/LoadAndUnloadList.vue'),
        meta: {
          permission: ['load-and-unload.list'],
        },
      },

      // SOF
      {
        path: 'shipping-order',
        name: 'adminShippingOrder',
        redirect: { name: 'adminShippingOrderList' },
        meta: {
          permission: ['sof.list'],
        },
        children: [
          {
            path: 'list',
            name: 'adminShippingOrderList',
            component: () => import('@/pages/admin/shipping-order/AdminShippingOrderList.vue'),
          },
          {
            path: ':code',
            name: 'adminCreateShippingOrder',
            props: true,
            meta: {
              permission: ['sof.view', 'sof.create'],
            },
            component: () => import('@/pages/admin/shipping-order/ShippingOrderFormDetails.vue'),
          },
        ],
      },

      // DR
      {
        path: 'delivery-receipt',
        name: 'adminDeliveryReceipt',
        redirect: { name: 'adminDeliveryReceiptList' },
        meta: {
          permission: ['dr.list'],
        },
        children: [
          {
            path: 'list',
            name: 'adminDeliveryReceiptList',
            component: () =>
              import('@/pages/admin/delivery-receipt-revised/DeliveryReceiptListing.vue'),
          },
          {
            path: ':code',
            name: 'adminCreateDeliveryReceipt',
            props: true,
            meta: {
              permission: ['dr.create', 'dr.view'],
            },
            component: () =>
              import('@/pages/admin/delivery-receipt/DeliveryReceiptDetailsForm.vue'),
          },
        ],
      },

      // DR
      {
        path: 'invoice-summary',
        name: 'adminInvoiceSummary',
        redirect: { name: 'adminInvoiceSummaryList' },
        // meta: {
        //   permission: ['invoice.list'],
        // },
        children: [
          {
            path: 'list',
            name: 'adminInvoiceSummaryList',
            component: () => import('@/pages/admin/invoice-summary/InvoiceSummaryList.vue'),
          },
          {
            path: ':code',
            name: 'adminCreateInvoiceSummary',
            props: true,
            component: () => import('@/pages/admin/invoice-summary/InvoiceSummaryDetails.vue'),
          },
        ],
      },
      {
        path: 'approvers',
        name: 'approvers',
        component: () => import('@/pages/admin/setup/approver/ApproverList.vue'),
      },
      {
        path: '/registration-links',
        name: 'registrationLinksPage',
        component: () => import('@/pages/admin/setup/links/RegistrationLinks.vue'),
        meta: {
          permission: ['registration.list'],
        },
      },
      {
        path: 'profile/:code?',
        name: 'adminProfile',
        props: true,
        component: () => import('@/pages/profile/ProfilePage.vue'),
      },
      {
        path: 'users',
        name: 'usersPage',
        redirect: { name: 'usersListing' },
        meta: {
          permission: ['users.list'],
        },
        children: [
          {
            path: '',
            name: 'usersListing',
            component: () => import('@/pages/admin/setup/users/UsersPage.vue'),
          },
          {
            path: ':code',
            name: 'userDetails',
            props: true,
            component: () => import('@/pages/profile/ProfilePage.vue'),
            meta: {
              permission: ['users.create', 'users.view'],
            },
          },
        ],
      },
    ],
  },

  // 404 Page
  {
    path: '/:pathMatch(.*)*',
    name: 'Page404',
    component: () => import('@/pages/Error.vue'),
  },
  {
    path: '/under-maintenance',
    name: 'UnderMaintenance',
    component: () => import('@/pages/UnderMaintenance.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const permissionStore = usePermissionStore()
  const isLoggedIn = authStore.isLoggedIn
  const user = authStore.getUserLogInfo
  const isProduction = false
  const guestRoutes = ['login', 'signUp', 'forgotPassword', 'ClientRegistration']

  if (isLoggedIn && guestRoutes.includes(to.name?.toString())) {
    const userType = user?.user_type
    return next({ name: userType === 'AD' ? 'adminDashboard' : 'clientDashboard' })
  }

  if (to.meta.requiresAuth && !isLoggedIn) {
    return next({ name: 'login' })
  }

  if (isLoggedIn) {
    const userType = user?.user_type
    const isOTPValidated = user?.otp_validated
    const requiredRole = to.meta.role

    if (isProduction && !isOTPValidated && to.name !== 'otp') {
      return next({ name: 'otp' })
    }

    if (requiredRole && userType && requiredRole !== userType) {
      return next({ name: userType === 'AD' ? 'adminDashboard' : 'clientDashboard' })
    }

    if (userType === 'AD' && !permissionStore.isLoaded) {
      await permissionStore.getPermissions()
    }

    const requiredPermission = to.meta.permission
    if (requiredPermission) {
      const hasAccess = permissionStore.hasAnyPermission(requiredPermission)

      if (!hasAccess) {
        return next(resolveFallback(routes, to, from, permissionStore, user))
      }

      const withCode = to?.params?.code != null
      if (withCode) {
        const isCreate = to?.params?.code === 'create'
        const requiredPerm = requiredPermission.find((perm) =>
          perm.endsWith(isCreate ? '.create' : '.view')
        )
        const isPermitted = permissionStore.hasPermission(requiredPerm)
        if (!isPermitted) {
          return next(resolveFallback(routes, to, from, permissionStore, user))
        }
      }
    }
  }

  return next()
})

const LAST_ROUTE_KEY = 'lastVisitedRoute'
const GUEST_ROUTES = ['login', 'signUp', 'forgotPassword', 'ClientRegistration', 'otp']

router.afterEach((to) => {
  if (!to.name || GUEST_ROUTES.includes(to.name.toString())) return
  try {
    sessionStorage.setItem(LAST_ROUTE_KEY, to.fullPath)
  } catch {
    /* TODO: Handle storage unavailable */
  }
})

export default router

function getNearestAccessibleRoute(routesConfig, permissionStore, targetRoute, user) {
  const userType = user?.user_type

  const basePath = userType === 'AD' ? '/admin' : '/client'
  const baseConfig = routesConfig.find((r) => r.path === basePath)

  if (!baseConfig || !baseConfig.children) return { name: 'Page404' }

  const findFirstValidRoute = (children) => {
    for (const route of children) {
      if (route.meta?.permission && !permissionStore.hasAnyPermission(route.meta.permission)) {
        continue
      }

      if (route.name && !route.path.includes(':') && route.name !== targetRoute.name) {
        return { name: route.name }
      }

      if (route.children && route.children.length > 0) {
        const validChild = findFirstValidRoute(route.children)
        if (validChild) return validChild
      }
    }
    return null
  }

  const fallback = findFirstValidRoute(baseConfig.children)

  const profileRouteName = userType === 'AD' ? 'adminProfile' : 'clientProfile'

  return (
    fallback || {
      name: profileRouteName,
      params: { code: user?.user_name },
    }
  )
}

function resolveFallback(routes, to, from, permissionStore, user) {
  if (from?.name && from.name !== to.name && isRouteAccessible(from, permissionStore)) {
    return { name: from.name, params: from.params, query: from.query }
  }

  const lastPath = getLastVisitedPath()
  if (lastPath && lastPath !== to.fullPath) {
    const resolved = router.resolve(lastPath)
    if (resolved?.name && isRouteAccessible(resolved, permissionStore)) {
      return lastPath
    }
  }

  const sibling = findAccessibleSibling(routes, to, permissionStore)
  if (sibling) {
    return { name: sibling.name }
  }

  return getNearestAccessibleRoute(routes, permissionStore, to, user)
}

function getLastVisitedPath() {
  try {
    return sessionStorage.getItem(LAST_ROUTE_KEY)
  } catch {
    return null
  }
}

function isRouteAccessible(route, permissionStore) {
  const permission = route.meta?.permission
  if (!permission) return true
  return permissionStore.hasAnyPermission(permission)
}

function findAccessibleSibling(routes, to, permissionStore) {
  const parentMatch = to.matched?.length >= 2 ? to.matched[to.matched.length - 2] : null
  if (!parentMatch?.name) return null

  const parentRoute = findRouteByName(routes, parentMatch.name)
  if (!parentRoute?.children?.length) return null

  const sibling = findFirstAccessibleChild(parentRoute.children, permissionStore)
  if (sibling && sibling.name !== to.name) {
    return sibling
  }
  return null
}

function findFirstAccessibleChild(children, permissionStore) {
  for (const child of children) {
    if (!child.name || child.path?.includes(':')) {
      continue
    }

    const permissions = child.meta?.permission || child.permission
    if (!permissions) {
      return child
    }

    if (permissionStore.hasAnyPermission(permissions)) {
      return child
    }
  }
  return null
}

function findRouteByName(routes, targetName) {
  for (const route of routes) {
    if (route.name === targetName) {
      return route
    }
    if (route.children) {
      const found = findRouteByName(route.children, targetName)
      if (found) return found
    }
  }
  return null
}
